import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Surface } from '#pplx/frontend/aether/components/Surface/index.js';
import { PortalPrimitive } from '#pplx/frontend/aether/primitives/PortalPrimitive/index.js';
import { SPLIT_PANE_WIDTHS_KEY, SplitPaneContext, useSplitPaneInternal, clampPaneWidth, PRIMARY_WIDTH_VAR, getSavedWidth, getSecondaryWidth, shouldUseSplitPaneOverlay, } from '#pplx/frontend/apps/ask/split-pane/SplitPaneShared.js';
import { useLocalStorage } from '#pplx/frontend/browser-storage/hooks/useLocalStorage.js';
import { useDesktopRegion } from '#pplx/frontend/components/overlays/desktopRegion.js';
import Modal from '#pplx/frontend/components/overlays/Modal.js';
import { useIsMobile } from '#pplx/frontend/platform-components/useMobileStyle.js';
import classNames from 'classnames';
import React, { Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
const Root = React.memo(function Root({ children, isOpen, resizable = true, defaultWidthPx, widthAnchor = 'secondary', }) {
    const widthKey = `${widthAnchor}:${defaultWidthPx}`;
    const [savedWidths, setSavedWidths] = useLocalStorage(SPLIT_PANE_WIDTHS_KEY, {});
    const [anchorWidthPx, setAnchorWidthPx] = useState(getSavedWidth({ widths: savedWidths, widthKey, resizable }) ?? defaultWidthPx);
    const [isPrimaryCollapsed, setIsPrimaryCollapsed] = useState(false);
    const [availableWidthPx, setAvailableWidthPx] = useState(typeof window === 'undefined' ? 0 : window.innerWidth);
    const [minimumPrimaryWidthPx, setMinimumPrimaryWidthPx] = useState(0);
    const primaryElementRef = useRef(null);
    const secondaryElementRef = useRef(null);
    const observerRef = useRef(null);
    const primaryWidthRef = useRef(typeof window === 'undefined' ? 0 : window.innerWidth);
    // Stable ref for the scrollable container (used by autoscroll hooks)
    const scrollableContainerRef = useRef(null);
    const calcWidth = useCallback(() => {
        const primaryWidth = primaryElementRef.current?.getBoundingClientRect().width ?? primaryWidthRef.current;
        primaryWidthRef.current = primaryWidth;
        setAvailableWidthPx(primaryWidth);
        [primaryElementRef.current, secondaryElementRef.current].forEach((element) => {
            element?.style.setProperty(PRIMARY_WIDTH_VAR, `${primaryWidth}px`);
        });
    }, []);
    const setWidth = useCallback((width, save = false) => {
        const availableWidth = primaryWidthRef.current;
        const nextWidth = clampPaneWidth({
            widthPx: widthAnchor === 'primary' ? availableWidth - width : width,
            availableWidth,
        });
        setAnchorWidthPx(nextWidth);
        if (save) {
            setSavedWidths((prev) => ({ ...(prev ?? {}), [widthKey]: nextWidth }));
        }
    }, [setSavedWidths, widthAnchor, widthKey]);
    useEffect(() => {
        setAnchorWidthPx(getSavedWidth({ widths: savedWidths, widthKey, resizable }) ?? defaultWidthPx);
    }, [defaultWidthPx, resizable, savedWidths, widthKey]);
    useEffect(() => {
        if (!isOpen) {
            setIsPrimaryCollapsed(false);
        }
    }, [isOpen]);
    const togglePrimaryCollapsed = useCallback(() => {
        setIsPrimaryCollapsed((prev) => !prev);
    }, []);
    const registerPrimaryElement = useCallback((element) => {
        if (primaryElementRef.current === element) {
            return;
        }
        primaryElementRef.current = element;
        observerRef.current?.disconnect();
        observerRef.current = null;
        if (element) {
            const observer = new ResizeObserver(calcWidth);
            // calcWidth measures the border box. The content box excludes the padding
            // derived from the last measurement, so it can stop changing while the
            // border box shrinks (e.g. zooming back in after zooming out) and leave
            // the secondary pane sized from a stale, wider primary.
            observer.observe(element, { box: 'border-box' });
            observerRef.current = observer;
        }
        calcWidth();
    }, [calcWidth]);
    const registerSecondaryElement = useCallback((element) => {
        secondaryElementRef.current = element;
        calcWidth();
    }, [calcWidth]);
    useEffect(() => () => observerRef.current?.disconnect(), []);
    const secondaryWidth = useMemo(() => getSecondaryWidth({ widthPx: anchorWidthPx, widthAnchor, minimumPrimaryWidthPx }), [anchorWidthPx, minimumPrimaryWidthPx, widthAnchor]);
    const isResponsiveOverlay = shouldUseSplitPaneOverlay({
        availableWidthPx,
        minimumPrimaryWidthPx,
    });
    const contextValue = useMemo(() => ({
        isOpen,
        isResponsiveOverlay,
        isPrimaryCollapsed,
        togglePrimaryCollapsed,
        setWidth,
        setMinimumPrimaryWidthPx,
        resizable,
        secondaryWidth,
        registerPrimaryElement,
        registerSecondaryElement,
        secondaryElementRef,
        scrollableContainerRef,
    }), [
        isOpen,
        isResponsiveOverlay,
        isPrimaryCollapsed,
        togglePrimaryCollapsed,
        secondaryWidth,
        setWidth,
        setMinimumPrimaryWidthPx,
        resizable,
        registerPrimaryElement,
        registerSecondaryElement,
        secondaryElementRef,
        scrollableContainerRef,
    ]);
    return _jsx(SplitPaneContext.Provider, { value: contextValue, children: children });
});
const ResizeHandle = memo(function ResizeHandle({ idleDivider = true }) {
    const { setWidth, resizable, secondaryElementRef } = useSplitPaneInternal();
    const [isResizing, setIsResizing] = useState(false);
    const cleanupRef = useRef(null);
    // Cleanup on unmount to prevent memory leaks
    useEffect(() => () => {
        if (cleanupRef.current) {
            cleanupRef.current();
        }
    }, []);
    const handleMouseDown = useCallback((e) => {
        if (!resizable)
            return;
        // Prevent default mousedown behavior — custom pane resize drag handling
        e.preventDefault();
        setIsResizing(true);
        // Prevent text selection and show resize cursor during drag
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        const startX = e.clientX;
        const startWidth = secondaryElementRef.current?.offsetWidth ?? 0;
        let lastWidth = startWidth;
        let didResize = false;
        const handleMouseMove = (e) => {
            const deltaX = e.clientX - startX;
            lastWidth = startWidth - deltaX;
            didResize = true;
            setWidth(lastWidth);
        };
        const cleanup = () => {
            if (didResize) {
                setWidth(lastWidth, true);
            }
            setIsResizing(false);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('contextmenu', handleMouseUp);
            cleanupRef.current = null;
        };
        const handleMouseUp = () => {
            cleanup();
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('contextmenu', handleMouseUp);
        // Store cleanup function in ref for potential unmount cleanup
        cleanupRef.current = cleanup;
    }, [setWidth, setIsResizing, secondaryElementRef, resizable]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: classNames('absolute inset-y-0 left-0 z-10 block', {
                    'group cursor-col-resize': resizable,
                }), children: [_jsx("div", { className: "absolute inset-y-0 left-0 h-full w-px", children: _jsx("div", { className: classNames('pointer-events-none absolute inset-0 origin-center', {
                                'bg-soft': idleDivider,
                                'group-hover:bg-inverse/15 group-hover:scale-x-[250%]': resizable,
                                '!bg-inverse/15 scale-x-[250%]': isResizing,
                            }) }) }), resizable && (_jsx("div", { className: "absolute top-0 h-full w-5 -translate-x-1/2 cursor-col-resize", onMouseDown: handleMouseDown }))] }), isResizing && _jsx("div", { className: "absolute inset-0 z-20 cursor-col-resize" })] }));
});
const SecondaryOverlay = ({ onClose, children, }) => {
    const { isOpen } = useSplitPaneInternal();
    return (_jsx(Modal, { variant: "full-sheet", isOpen: isOpen, onClose: onClose, headerClassname: "hidden", renderCloseButton: false, wrapperClassname: "!flex-1 !px-0 !pb-0", modalContentClassname: "min-h-0 !flex-1 !p-0", disableAnimation: true, delayClose: false, animationClassName: "!animate-none !duration-0", children: _jsx("div", { className: "flex h-full min-h-0 flex-1 flex-col", children: children }) }));
};
const SecondaryBox = ({ className, boxRef, children }) => {
    const { isOpen, isPrimaryCollapsed, secondaryWidth, registerSecondaryElement } = useSplitPaneInternal();
    const registerBox = useCallback((element) => {
        registerSecondaryElement(element);
        boxRef?.(element);
    }, [registerSecondaryElement, boxRef]);
    return (_jsx(PortalPrimitive, { children: _jsx(Surface, { ref: registerBox, className: classNames('fixed flex w-[60vw] min-w-0 flex-1 flex-col text-clip', className, {
                'pointer-events-none w-0 translate-x-full opacity-0': !isOpen,
            }), style: {
                width: isOpen
                    ? isPrimaryCollapsed
                        ? `var(${PRIMARY_WIDTH_VAR}, 100vw)`
                        : secondaryWidth
                    : undefined,
            }, children: children }) }));
};
function WebSecondary({ children, allowOverflow = false }) {
    const { isPrimaryCollapsed } = useSplitPaneInternal();
    return (_jsxs(SecondaryBox, { className: classNames('right-0 inset-y-0', { 'overflow-hidden': !allowOverflow }), children: [!isPrimaryCollapsed && _jsx(ResizeHandle, {}), children] }));
}
function DesktopSecondary({ children, allowOverflow = false, region = 'side-pane', desktopBoundsClassName, desktopCollapsedClassName, }) {
    const { isOpen, isPrimaryCollapsed } = useSplitPaneInternal();
    const { ref: reportRegion, presentation } = useDesktopRegion(region, {
        active: isOpen,
        expanded: isPrimaryCollapsed,
    });
    const Wrapper = presentation?.Wrapper ?? Fragment;
    const sheet = presentation?.sheet;
    if (sheet) {
        return (_jsx(SecondaryBox, { boxRef: reportRegion, className: isPrimaryCollapsed ? sheet.expandedBoxClassName : sheet.boxClassName, children: _jsx(Wrapper, { children: _jsx(sheet.Component, { expanded: isPrimaryCollapsed, resizeHandle: !isPrimaryCollapsed && _jsx(ResizeHandle, { idleDivider: false }), children: children }) }) }));
    }
    return (_jsxs(SecondaryBox, { boxRef: reportRegion, className: classNames('right-0', desktopBoundsClassName ?? 'inset-y-0', {
            'overflow-hidden': !allowOverflow,
            [desktopCollapsedClassName ?? '']: isPrimaryCollapsed,
        }), children: [!isPrimaryCollapsed && _jsx(ResizeHandle, {}), _jsx(Wrapper, { children: children })] }));
}
const Secondary = memo(function Secondary(props) {
    const { isMobileStyle } = useIsMobile();
    const { isResponsiveOverlay } = useSplitPaneInternal();
    if (isMobileStyle || isResponsiveOverlay) {
        return _jsx(SecondaryOverlay, { onClose: props.onClose, children: props.children });
    }
    return import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD ? (_jsx(DesktopSecondary, { ...props })) : (_jsx(WebSecondary, { ...props }));
});
const Primary = React.memo(function Primary({ children, minimumWidthPx = 0 }) {
    const { isOpen, isPrimaryCollapsed, isResponsiveOverlay, secondaryWidth, registerPrimaryElement, setMinimumPrimaryWidthPx, } = useSplitPaneInternal();
    const { isMobileStyle } = useIsMobile();
    useLayoutEffect(() => {
        setMinimumPrimaryWidthPx(minimumWidthPx);
        return () => setMinimumPrimaryWidthPx(0);
    }, [minimumWidthPx, setMinimumPrimaryWidthPx]);
    return (_jsx("div", { ref: registerPrimaryElement, className: classNames('flex size-full flex-1', {
            'pointer-events-none invisible': isPrimaryCollapsed,
        }), "aria-hidden": isPrimaryCollapsed || undefined, style: {
            paddingRight: isOpen && !isMobileStyle && !isResponsiveOverlay
                ? isPrimaryCollapsed
                    ? `var(${PRIMARY_WIDTH_VAR}, 100vw)`
                    : secondaryWidth
                : undefined,
        }, children: children }));
});
export const SplitPane = Object.assign({}, {
    Root,
    Primary,
    Secondary,
});
//# sourceMappingURL=SplitPane.js.map